import router from '../../router'
import NavigationBar from '../../components/navigation-bar/NavigationBar'
import Loader from '../../components/loader/Loader'
import moment from 'moment'
import {KeycloakService} from "@/services/keycloak.service";
import Card from '../../components/card/Card'
import {Location, LocationService} from "@/services/location.service";
import {Entity, EntityService} from "@/services/entity.service";
import {StatusService} from "@/services/status.service";
import {TimeSlotUtil} from "@/utils/timeslot.util";
import VisitsOptionsContainer from "../../components/visits-options-container/VisitsOptionsContainer";
import VisitsTimeSlotContainer from "../../components/visits-timeslots-container/VisitsTimeSlotContainer"
import {ScreenLoader} from "@/utils/loading.util";
import {AccountService} from "@/services/account.service";
import {BookingOption, BookingService} from "@/services/booking.service";
import locations from "@/views/settings/locations/locations";
import {CookieService} from "@/services/cookie.service";

export default {
    name: 'Visits',
    components: {
        NavigationBar,
        Loader,
        Card,
        VisitsOptionsContainer,
        VisitsTimeSlotContainer
    },

    data() {
        return {
            arrivalDate:  this.$route.query.date ? new Date(this.$route.query.date): new Date(),
            activeTimeSlots: [],
            screenLoader: new ScreenLoader(),
            entities: [],
            locations: [],
            location: new Location(),
            showCard: false,
            timeSlots: [],
            filteredEntities: [],
            entityMode: true,
            selectedBooking: undefined,
            spotBookings: [],
            spots: [],
            preferences: {},
            bookingOptions: [],
            bookingOption: new BookingOption({id: 0, name: "Default", options: {}}),
            defaultOptions: [
                new BookingOption({id: 100000, name: "Default", options: {}}),
                new BookingOption({id: 100001, name: "Customer", options: {}}),
                new BookingOption({id: 100002, name: "Front license plate", options: {}}),
                new BookingOption({id: 100003, name: "Rear license plate", options: {}})
            ]
        }
    },

    async mounted() {
        this.screenLoader.start(['filter'])
        await this.getAccount()
        await this.getLocations();
        this.preferences = CookieService.getCookie("preference");
        let locationId
        // check if cookie has been set (really only to catch first time opening page), if so set location else
        // pick it from the query params or the first location available
        if (this.preferences){
            if (this.preferences.location) {
                locationId = this.locations.find(location => location.id === this.preferences.location)?.id
                if (locationId == undefined) {
                    this.preferences = undefined
                    locationId = this.locations[0].id
                }
            } else {
                locationId = this.$route.query.locationid !== "undefined" && this.$route.query.locationid ? this.$route.query.locationid : this.locations[0].id
            }
        } else {
            locationId = this.$route.query.locationid !== "undefined" && this.$route.query.locationid? this.$route.query.locationid : this.locations[0].id
        }
        await this.updateLocationData(locationId, true)
        this.screenLoader.stop(['filter'])
    },

    computed: {
        isBookingAgent() {
            return KeycloakService.isBookingAgent()
        },
    },

    watch: {
        async location () {
            const options= await BookingService.getBookingOptions(this.location.id)
            this.bookingOption = this.defaultOptions[0]
            this.bookingOptions = [...this.defaultOptions, ...options]
        }
    },

    methods: {

        // triggered by loader when pressing reload after data fetch fails
        async reload() {
            await this.updateLocationData(this.location.id, false)
        },

        /**
         * Gets all the locations associated with account, stores them in this.locations
         * @returns {Promise<void>}
         */
        getLocations: async function() {
            const locationsResponse = await LocationService.getLocations().catch((error) => {
                console.log('error')
                this.screenLoader.stopWithError(error, ['filter'])
            })
            this.locations = locationsResponse.map(o => new Location(o))
        },

        /**
         * Function that handles most backend calls in the correct sequence, it is called when loading/reloading
         * the page and when changing dates using the datepicker.
         * @param locationId the location to get the data for
         * @param updateSpots whether to update the spots or not (false when triggered by arrivalDate updates, true when location updates)
         * @return {Promise<void>}
         */
        updateLocationData : async function (locationId, updateSpots) {
            this.screenLoader.start(['locations'])
            const metaDataResult = await this.getMetaData(locationId).catch((error) => {
                this.screenLoader.stopWithError(error, ['locations'])
            })

            await this.handleMetaDataResponse(metaDataResult)
            this.screenLoader.stop(['locations'])
        },


        /**
         * Get data associated with a location, (details about the location not in this.locations, the entities and status
         * for this location)
         * @param locationId
         * @returns {Promise<(*|undefined)[]>}
         */
        getMetaData: async function(locationId) {
            return await Promise.all([
                LocationService.getLocation(locationId),
                EntityService.getEntities(locationId, this.arrivalDate),
                StatusService.getStatus()
            ])
        },

        /**
         * Get all the active entities and active timeslots for a location
         *
         * @param location the selected location
         * @param entities the list of entities for the location
         * @return {Promise<void>}
         */
        handleMetaDataResponse([location, entities]) {
            this.timeSlots = []
            // guard to prevent updating this.location and triggering watch function in VisitOptionsContainer if location
            // stays the same
            if (this.location.id !== location.id) this.location = location

            const startTime = TimeSlotUtil.getTimeSlotTime(location.timeSlotStartTime)
            const endTime = TimeSlotUtil.getTimeSlotTime(location.timeSlotEndTime)
            this.timeSlots = TimeSlotUtil.createTimeSlots(startTime, endTime, location.timeSlotLengthInMinutes, false)
            entities = entities.filter(entity => entity.active)

            entities = this.sortEntities(entities)

            this.activeTimeSlots = TimeSlotUtil.createActiveTimeSlots(this.arrivalDate, startTime, endTime, entities)

            for (let i = 0; i < entities.length; i++) {
                entities[i] = new Entity(entities[i])
                entities[i].createBookingTimeSlots(this.activeTimeSlots[i], entities[i].timeSlotLengthInMinutes / this.location.timeSlotLengthInMinutes)
            }
            this.entities = entities

            const start = moment(this.arrivalDate).startOf('day').toISOString()
            if (start !== this.$route.query.date || this.location.id !== this.$route.query.locationid) {
                router.push(`/visits?locationid=${this.location.id}&date=${start}`)
            }
        },

        sortEntities (entities) {
           return entities.sort((a, b) => {
                if ( a.name < b.name ){
                    return -1;
                }
                if ( a.name > b.name ){
                    return 1;
                }
                return 0;
            })
        },

        // set the account customers
        getAccount: async function() {
            let account = await AccountService.getLoggedInAccount().catch((error) => {
                this.screenLoader.stopWithError(error, ['filter'])
            })
            this.accountCustomers = account.customers
        },

        /**
         * Triggered when a location is clicked in the location dropDown, call updateLocationData with the id of
         * the new location
         * @param id the id of the selected location
         * @returns {Promise<void>}
         */
        updateLocation: async function(id) {
            await this.updateLocationData(id, true);
        },

        updateBookingOption: function(id) {
            this.bookingOption = this.bookingOptions.find(o => o.id === id)
        },

        /**
         * Triggerd when a new date is picked in the date selector, update the location data for this new location
         * Do not update spots since location has not changed
         * @param date
         * @returns {Promise<void>}
         */
        updateArrivalDate: async function(date){
            // check if new date is picked
            if (this.arrivalDate !== date) {
                this.arrivalDate = date;
            }
            await this.updateLocationData(this.location.id, false);
        },

        /**
         * Triggered by VisitOptionsContainer when entities are filtered, this.filteredEntities update triggers re-render
         * of VisitTimeSlotContainer
         * @param entities the selected entities
         */
        updateFilteredEntities: function(entities) {
            this.filteredEntities = entities
        },

        /**
         * Triggered by VisitOptionsContainer when the entityMode is updated, this.entityMode update triggers re-render
         * of VisitTimeSlotContainer
         * @param mode true if entityMode false if showing spots
         */
        changeEntityMode(mode) {
            this.entityMode = mode;
        },

        /**
         * Shows card with selectedBooking info
         * @param entityIndex
         * @param timeSlotIndex
         */
        hover(entityIndex, timeSlotIndex) {
            const entity = this.entities[entityIndex]
            const startTimeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]

            const booking = TimeSlotUtil.getTimeSlotBooking(entity.bookings, startTimeSlot, this.arrivalDate)
            if (booking && booking.customer !== undefined) {
                this.selectedBooking = booking
                this.toggleCard()
            }
        },

        // toggles card
        toggleCard() {
            this.showCard = true
        }
    }
}
